import { router } from '@inertiajs/react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

interface LeadNewProps {
  errors?: {
    name?: string[]
    phone?: string[]
    source?: string[]
    source_id?: string[]
    vehicle_type?: string[]
    issue_description?: string[]
    urgency?: string[]
  }
}

const leadSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  phone: z.string().optional(),
  email: z.string().email('Invalid email format').optional().or(z.literal('')),
  source: z.enum(['Phone', 'Instagram', 'Facebook Messenger', 'Telegram', 'WhatsApp', 'YouTube', 'Website', 'Referral', 'Walk-in']),
  source_id: z.string().optional(),
  vehicle_type: z.string().optional(),
  issue_description: z.string().optional(),
  feedback: z.string().optional(),
  urgency: z.enum(['Low', 'Medium', 'High', 'Emergency']),
}).refine((data) => {
  // Phone is required for Phone source
  if (data.source === 'Phone' && !data.phone) {
    return false
  }
  return true
}, {
  message: 'Phone number is required when source is Phone',
  path: ['phone'],
}).refine((data) => {
  // Source ID is required for social media sources
  const socialSources = ['Instagram', 'Facebook Messenger', 'Telegram', 'WhatsApp', 'YouTube']
  if (socialSources.includes(data.source) && !data.source_id) {
    return false
  }
  return true
}, {
  message: 'Social media ID is required for this source type',
  path: ['source_id'],
})

type LeadFormData = z.infer<typeof leadSchema>

export default function LeadNew({ errors }: LeadNewProps) {
  const { register, handleSubmit, setValue, watch, formState: { errors: formErrors, isSubmitting } } = useForm<LeadFormData>({
    resolver: zodResolver(leadSchema),
    defaultValues: {
      source: 'Phone',
      urgency: 'Medium',
    }
  })

  const selectedSource = watch('source')
  const socialSources = ['Instagram', 'Facebook Messenger', 'Telegram', 'WhatsApp', 'YouTube']
  const requiresSourceId = socialSources.includes(selectedSource)
  const requiresPhone = selectedSource === 'Phone'

  const onSubmit = (data: LeadFormData) => {
    // Transform data to match backend expectations
    const transformedData = {
      name: data.name,
      phone: data.phone || undefined,
      email: data.email || undefined,
      source: data.source,
      source_id: data.source_id || undefined,
      vehicle_type: data.vehicle_type || undefined,
      issue_description: data.issue_description || undefined,
      feedback: data.feedback || undefined,
      urgency: data.urgency.toLowerCase(),
      status: 'new',
    }
    router.post('/leads', { lead: transformedData })
  }

  const getSourceIdPlaceholder = () => {
    switch (selectedSource) {
      case 'Instagram':
        return '@username'
      case 'Facebook Messenger':
        return 'Facebook profile name'
      case 'Telegram':
        return '@username'
      case 'WhatsApp':
        return 'WhatsApp number'
      case 'YouTube':
        return '@channelname'
      default:
        return 'Source identifier'
    }
  }

  return (
    <div className="flex flex-1 flex-col min-w-0">
      <div className="@container/main flex flex-1 flex-col gap-2 min-w-0">
        <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 min-w-0">
          <div className="px-4 lg:px-6">
            <Card className="rounded-2xl">
              <CardHeader>
                <CardTitle>Create New Lead</CardTitle>
                <CardDescription>Add a new customer lead to your pipeline</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  {/* Customer Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Customer Information</h3>

                    <div className="space-y-2">
                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        type="text"
                        placeholder="John Doe or just John"
                        {...register('name')}
                        data-testid="lead-name-input"
                      />
                      {formErrors.name && (
                        <p className="text-sm text-destructive">{formErrors.name.message}</p>
                      )}
                      {errors?.name && (
                        <p className="text-sm text-destructive">{errors.name[0]}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="source">Source *</Label>
                      <Select
                        value={watch('source')}
                        onValueChange={(value) => setValue('source', value as any)}
                      >
                        <SelectTrigger id="source" data-testid="lead-source-select">
                          <SelectValue placeholder="Select source" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Phone">Phone</SelectItem>
                          <SelectItem value="Instagram">Instagram</SelectItem>
                          <SelectItem value="Facebook Messenger">Facebook Messenger</SelectItem>
                          <SelectItem value="Telegram">Telegram</SelectItem>
                          <SelectItem value="WhatsApp">WhatsApp</SelectItem>
                          <SelectItem value="YouTube">YouTube</SelectItem>
                          <SelectItem value="Website">Website</SelectItem>
                          <SelectItem value="Referral">Referral</SelectItem>
                          <SelectItem value="Walk-in">Walk-in</SelectItem>
                        </SelectContent>
                      </Select>
                      {formErrors.source && (
                        <p className="text-sm text-destructive">{formErrors.source.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="contact_info">ID or Phone</Label>
                      <Input
                        id="contact_info"
                        type="text"
                        placeholder={requiresPhone ? "(555) 123-4567" : requiresSourceId ? getSourceIdPlaceholder() : "Phone or social media ID"}
                        value={requiresPhone ? watch('phone') || '' : watch('source_id') || ''}
                        onChange={(e) => {
                          if (requiresPhone) {
                            setValue('phone', e.target.value)
                          } else {
                            setValue('source_id', e.target.value)
                          }
                        }}
                        data-testid="lead-contact-info-input"
                      />
                      {(formErrors.phone || formErrors.source_id) && (
                        <p className="text-sm text-destructive">
                          {formErrors.phone?.message || formErrors.source_id?.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">Email (optional)</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="customer@example.com (optional)"
                        {...register('email')}
                        data-testid="lead-email-input"
                      />
                      {formErrors.email && (
                        <p className="text-sm text-destructive">{formErrors.email.message}</p>
                      )}
                      <p className="text-xs text-muted-foreground">Email is optional. Phone or social media ID is the main contact method.</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="urgency">Urgency *</Label>
                      <Select
                        value={watch('urgency')}
                        onValueChange={(value) => setValue('urgency', value as any)}
                      >
                        <SelectTrigger id="urgency" data-testid="lead-urgency-select">
                          <SelectValue placeholder="Select urgency" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Low">Low</SelectItem>
                          <SelectItem value="Medium">Medium</SelectItem>
                          <SelectItem value="High">High</SelectItem>
                          <SelectItem value="Emergency">Emergency</SelectItem>
                        </SelectContent>
                      </Select>
                      {formErrors.urgency && (
                        <p className="text-sm text-destructive">{formErrors.urgency.message}</p>
                      )}
                    </div>
                  </div>

                  {/* Vehicle & Issue Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Vehicle & Issue</h3>

                    <div className="space-y-2">
                      <Label htmlFor="vehicle_type">Vehicle Type</Label>
                      <Input
                        id="vehicle_type"
                        type="text"
                        placeholder="e.g., Semi-Truck, Box Truck, Pickup, Trailer (optional)"
                        {...register('vehicle_type')}
                        data-testid="lead-vehicle-type-input"
                      />
                      {formErrors.vehicle_type && (
                        <p className="text-sm text-destructive">{formErrors.vehicle_type.message}</p>
                      )}
                      {errors?.vehicle_type && (
                        <p className="text-sm text-destructive">{errors.vehicle_type[0]}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="issue_description">Issue Description</Label>
                      <Textarea
                        id="issue_description"
                        placeholder="Describe the issue in detail (optional)..."
                        rows={4}
                        {...register('issue_description')}
                        data-testid="lead-issue-input"
                      />
                      {formErrors.issue_description && (
                        <p className="text-sm text-destructive">{formErrors.issue_description.message}</p>
                      )}
                      {errors?.issue_description && (
                        <p className="text-sm text-destructive">{errors.issue_description[0]}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="feedback">Feedback</Label>
                      <Textarea
                        id="feedback"
                        placeholder="Optional: Add any customer feedback or internal notes..."
                        rows={3}
                        {...register('feedback')}
                        data-testid="lead-feedback-input"
                      />
                      {formErrors.feedback && (
                        <p className="text-sm text-destructive">{formErrors.feedback.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => router.visit('/leads')}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="rounded-full bg-[#84b9ef] hover:bg-[#6fa8e0] text-white shadow-md"
                      data-testid="submit-lead-button"
                    >
                      {isSubmitting ? 'Creating...' : 'Create Lead'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}
